/**
 * Onboarding Viewport System — v2
 * 
 * Adaptive scene composition engine for onboarding screens.
 * 
 * DESIGN PRINCIPLE:
 *   STANDARD is the baseline — it reproduces the original hardcoded values exactly.
 *   SMALL applies minimal compression — never shrinks the emotional weight.
 *   LARGE gracefully expands — more breathing room, slightly larger elements.
 *   TABLET centers the mobile canvas at LARGE sizing.
 * 
 * This is NOT proportional scaling. It is intentional scene redistribution.
 * 
 * VIEWPORT CLASSES:
 *   SMALL    — iPhone SE / mini / 8              (height < 700px)
 *   STANDARD — iPhone 14 / 15 / regular Pro      (height 700–844px)
 *   LARGE    — iPhone Plus / Max / Pro Max        (height > 844px)
 *   TABLET   — iPad / Desktop (centered canvas)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

// ─── Viewport Classification ───────────────────────────────────────────────

const BREAKPOINTS = {
  SMALL_MAX_HEIGHT: 700,
  STANDARD_MAX_HEIGHT: 844,
  LARGE_MAX_HEIGHT: 1000,
  TABLET_MIN_WIDTH: 768,
};

function classifyViewport(width, height) {
  if (width >= BREAKPOINTS.TABLET_MIN_WIDTH) return 'TABLET';
  if (height <= BREAKPOINTS.SMALL_MAX_HEIGHT) return 'SMALL';
  if (height <= BREAKPOINTS.STANDARD_MAX_HEIGHT) return 'STANDARD';
  return 'LARGE';
}

// ─── Adaptive Spacing Tokens ────────────────────────────────────────────────
// STANDARD = original hardcoded values (the baseline that looked good).
// SMALL = slight compression only — preserve density and immersion.
// LARGE = graceful expansion — redistribute space, slightly larger elements.

function getSpacingTokens(vpClass) {
  switch (vpClass) {
    case 'SMALL':
      return {
        screenPaddingH: 16,
        headerPaddingTop: 10,
        headerPaddingBottom: 12,
        sectionGap: 14,
        headlineMarginTop: 0,
        heroMarginTop: 4,
        cardsGap: 6,
        cardsMarginTop: 8,
        ctaPaddingBottom: 10,
        ctaPaddingTop: 10,
        ctaHeight: 56,
        ctaBorderRadius: 18,
        ctaFontSize: 18,
        footerPaddingBottom: 8,
      };
    case 'STANDARD':
      // === BASELINE: matches original hardcoded values ===
      return {
        screenPaddingH: 20,
        headerPaddingTop: 12,
        headerPaddingBottom: 16,
        sectionGap: 16,
        headlineMarginTop: 0,
        heroMarginTop: 6,
        cardsGap: 8,
        cardsMarginTop: 12,
        ctaPaddingBottom: 10,
        ctaPaddingTop: 10,
        ctaHeight: 60,
        ctaBorderRadius: 18,
        ctaFontSize: 19,
        footerPaddingBottom: 10,
      };
    case 'LARGE':
      return {
        screenPaddingH: 22,
        headerPaddingTop: 16,
        headerPaddingBottom: 20,
        sectionGap: 24,
        headlineMarginTop: 8,
        heroMarginTop: 16,
        cardsGap: 16,
        cardsMarginTop: 24,
        ctaPaddingBottom: 14,
        ctaPaddingTop: 14,
        ctaHeight: 64,
        ctaBorderRadius: 20,
        ctaFontSize: 20,
        footerPaddingBottom: 14,
      };
    case 'TABLET':
      return {
        screenPaddingH: 24,
        headerPaddingTop: 10,
        headerPaddingBottom: 12,
        sectionGap: 18,
        headlineMarginTop: 6,
        heroMarginTop: 6,
        cardsGap: 10,
        cardsMarginTop: 16,
        ctaPaddingBottom: 16,
        ctaPaddingTop: 16,
        ctaHeight: 64,
        ctaBorderRadius: 20,
        ctaFontSize: 20,
        footerPaddingBottom: 16,
      };
    default:
      return getSpacingTokens('STANDARD');
  }
}

// ─── Scene Composition Values ───────────────────────────────────────────────
// These drive mascot size, hero region, headlines.
// STANDARD = original values. SMALL slightly tighter. LARGE slightly expanded.

function getCompositionValues(vpClass) {
  switch (vpClass) {
    case 'SMALL':
      return {
        // Slight compression — never tiny, never crushed
        mascotScale: 0.92,
        heroHeight: 140,
        heroMaxHeight: 180,
        headlineFontSize: 'clamp(26px, 7.5vw, 30px)',
        subHeadlineFontSize: 'clamp(14px, 4vw, 15px)',
        cardIconSize: 66,
        canvasMaxWidth: 440,
      };
    case 'STANDARD':
      // === BASELINE: reproduces original hardcoded sizes ===
      return {
        mascotScale: 1.0,
        heroHeight: 160,
        heroMaxHeight: 200,
        headlineFontSize: 'clamp(28px, 8vw, 32px)',
        subHeadlineFontSize: 'clamp(13px, 4vw, 15px)',
        cardIconSize: 72,
        canvasMaxWidth: 460,
      };
    case 'LARGE':
      // Graceful expansion — more presence, more breathing room
      return {
        mascotScale: 1.08,
        heroHeight: 190,
        heroMaxHeight: 240,
        headlineFontSize: 'clamp(30px, 8.5vw, 36px)',
        subHeadlineFontSize: 'clamp(14px, 4.5vw, 16px)',
        cardIconSize: 76,
        canvasMaxWidth: 500,
      };
    case 'TABLET':
      return {
        mascotScale: 1.1,
        heroHeight: 220,
        heroMaxHeight: 260,
        headlineFontSize: '36px',
        subHeadlineFontSize: '16px',
        cardIconSize: 76,
        canvasMaxWidth: 540,
      };
    default:
      return getCompositionValues('STANDARD');
  }
}

// ─── The Hook ───────────────────────────────────────────────────────────────

export function useOnboardingViewport() {
  const getInitialDimensions = () => {
    if (typeof window === 'undefined') {
      return { width: 375, height: 812 };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  };

  const [dimensions, setDimensions] = useState(getInitialDimensions);
  const [isReady, setIsReady] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const vpClass = classifyViewport(dimensions.width, dimensions.height);
  const spacing = getSpacingTokens(vpClass);
  const composition = getCompositionValues(vpClass);

  /**
   * sp() — Adaptive value selector.
   * Pick a value per viewport class without verbose switch statements.
   * 
   * Usage: vp.sp(smallVal, standardVal, largeVal)
   * If only 2 args: sp(small, rest)
   */
  const sp = useCallback((small, standard, large) => {
    if (large === undefined) {
      return vpClass === 'SMALL' ? small : standard;
    }
    switch (vpClass) {
      case 'SMALL': return small;
      case 'STANDARD': return standard;
      case 'LARGE':
      case 'TABLET':
        return large;
      default: return standard;
    }
  }, [vpClass]);

  return useMemo(() => ({
    isReady,
    class: vpClass,
    isSmall: vpClass === 'SMALL',
    isStandard: vpClass === 'STANDARD',
    isLarge: vpClass === 'LARGE' || vpClass === 'TABLET',
    isTablet: vpClass === 'TABLET',

    width: dimensions.width,
    height: dimensions.height,

    spacing,
    ...composition,

    sp,
  }), [vpClass, dimensions, spacing, composition, sp]);
}

export function getViewportClass() {
  if (typeof window === 'undefined') return 'STANDARD';
  return classifyViewport(window.innerWidth, window.innerHeight);
}
